import { useEffect, useState } from "react";
import { FileText, DollarSign, TrendingUp, AlertCircle } from "lucide-react";
import { getAmbassadorManagement, type AmbassadorDashboard } from "../../../api/admin";
import { Button } from "../../primitives/Button";

type AmbassadorManagementTabProps = {
  ambassadorId: string;
  dashboard: AmbassadorDashboard;
};

export function AmbassadorManagementTab({ ambassadorId, dashboard }: AmbassadorManagementTabProps) {
  const [management, setManagement] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getAmbassadorManagement(ambassadorId)
      .then((data) => {
        if (!mounted) return;
        setManagement(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load management data:", error);
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [ambassadorId]);

  if (loading) {
    return (
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="text-sm text-muted">Chargement...</div>
      </div>
    );
  }

  const contractData = management?.contract || {
    type: "standard",
    signed_date: null,
    duration_months: null,
    status: "pending",
    documents: [],
    clauses: [],
  };

  const pricingData = management?.pricing || {
    tier: "silver",
    min_credits_commit: 0,
    discount_percent: 0,
    price_book: null,
    overrides: null,
    progression_to_tier: 0,
    eligible_upgrade: false,
  };

  return (
    <div className="space-y-6">
      {/* A. Contrat */}
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText size={16} strokeWidth={1.5} />
            <h3 className="text-sm font-semibold text-text">Contrat / Entente</h3>
          </div>
          <Button label="Modifier" intent="default" size="sm" onClick={() => alert("À implémenter")} />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs text-muted mb-1">Type entente</div>
            <div className="text-sm font-medium text-text">{contractData.type || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Date signature</div>
            <div className="text-sm font-medium text-text">
              {contractData.signed_date
                ? new Date(contractData.signed_date).toLocaleDateString("fr-CA")
                : "Non signé"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Durée</div>
            <div className="text-sm font-medium text-text">
              {contractData.duration_months ? `${contractData.duration_months} mois` : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Statut</div>
            <div className="text-sm font-medium text-text">{contractData.status || "—"}</div>
          </div>
        </div>

        {contractData.documents && contractData.documents.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="text-xs text-muted mb-2">Documents attachés</div>
            <div className="space-y-1">
              {contractData.documents.map((doc: any, idx: number) => (
                <div key={idx} className="text-sm text-text">
                  {doc.name || `Document ${idx + 1}`}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* B. Pricing & Tier */}
      <div className="rounded-md border border-border bg-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DollarSign size={16} strokeWidth={1.5} />
            <h3 className="text-sm font-semibold text-text">Pricing & Tier</h3>
          </div>
          <Button label="Modifier" intent="default" size="sm" onClick={() => alert("À implémenter")} />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs text-muted mb-1">Tier actuel</div>
            <div className="text-sm font-medium text-text capitalize">{pricingData.tier || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Min credits commit</div>
            <div className="text-sm font-medium text-text">
              {pricingData.min_credits_commit?.toLocaleString() || "0"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Discount %</div>
            <div className="text-sm font-medium text-text">
              {pricingData.discount_percent ? `${pricingData.discount_percent}%` : "0%"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Price book</div>
            <div className="text-sm font-medium text-text">{pricingData.price_book || "—"}</div>
          </div>
        </div>

        {/* Indicateurs */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs text-muted mb-1">Progression vers seuil tier</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-info"
                    style={{ width: `${Math.min(pricingData.progression_to_tier || 0, 100)}%` }}
                  />
                </div>
                <div className="text-sm font-medium text-text">
                  {Math.round(pricingData.progression_to_tier || 0)}%
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted mb-1">Eligible upgrade ?</div>
              <div className="text-sm font-medium text-text">
                {pricingData.eligible_upgrade ? (
                  <span className="text-success">Oui</span>
                ) : (
                  <span className="text-muted">Non</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
